import { useState, useEffect } from 'react';
import { Bot, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import MistralClient from '@mistralai/mistralai';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const MISTRAL_API_KEY = import.meta.env.VITE_MISTRAL_API_KEY;

export default function AISettings() {
  const { user } = useAuth();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');
  const [chatbotVisible, setChatbotVisible] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  const loadChatbotVisibility = async () => {
    try {
      const stored = localStorage.getItem('chatbot_visible');
      if (stored !== null) {
        setChatbotVisible(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Error loading chatbot visibility:', err);
    }
  };

  // Charger la préférence de visibilité au démarrage
  useEffect(() => {
    loadChatbotVisibility();
  }, []);

  const handleVisibilityChange = async (visible: boolean) => {
    try {
      setSavingVisibility(true);
      setChatbotVisible(visible);
      
      // Sauvegarder dans le localStorage
      localStorage.setItem('chatbot_visible', JSON.stringify(visible));
      
      // Déclencher un événement personnalisé pour notifier le ChatBot
      window.dispatchEvent(new CustomEvent('chatbot-visibility-changed', { 
        detail: { visible } 
      }));
      
    } catch (err) {
      console.error('Error saving chatbot visibility:', err);
    } finally {
      setSavingVisibility(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setTestMessage('');

    try {
      if (!MISTRAL_API_KEY) {
        throw new Error('Clé API Mistral non configurée');
      }

      const client = new MistralClient(MISTRAL_API_KEY);
      
      // Test simple avec une question basique
      const response = await client.chat({
        model: 'mistral-medium',
        messages: [{ role: 'user', content: 'Bonjour' }],
        temperature: 0.7,
        max_tokens: 100
      });

      if (response?.choices?.[0]?.message?.content) {
        setTestResult('success');
        setTestMessage('Connexion réussie à l\'API Mistral');
      } else {
        throw new Error('Réponse invalide de l\'API');
      }
    } catch (err: any) {
      console.error('Error testing Mistral connection:', err);
      setTestResult('error');
      setTestMessage(err.message || 'Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Configuration de l'IA</h2>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-6">
          {/* Statut de la configuration */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">
              Configuration actuelle
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bot className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">API Mistral</p>
                      <p className="text-xs text-gray-500">
                        {MISTRAL_API_KEY ? 'Clé API configurée' : 'Clé API non configurée'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {testResult === 'success' && (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-green-600">Connecté</span>
                      </>
                    )}
                    {testResult === 'error' && (
                      <>
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-red-600">Erreur</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Option de visibilité du ChatBot */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bot className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Visibilité K-BOT</p>
                      <p className="text-xs text-gray-500">
                        Afficher ou masquer l'icône du chatbot K-BOT
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={chatbotVisible}
                        onChange={(e) => handleVisibilityChange(e.target.checked)}
                        disabled={savingVisibility}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className={`text-sm font-medium ${chatbotVisible ? 'text-green-600' : 'text-gray-500'}`}>
                      {chatbotVisible ? 'Visible' : 'Masqué'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Test de connexion */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">
              Test de connexion
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Bot className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">API Mistral</p>
                    <p className="text-xs text-gray-500">
                      {MISTRAL_API_KEY ? 'Clé API configurée' : 'Clé API non configurée'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {testResult === 'success' && (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">Connecté</span>
                    </>
                  )}
                  {testResult === 'error' && (
                    <>
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-red-600">Erreur</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <button
                onClick={testConnection}
                disabled={testing || !MISTRAL_API_KEY}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Test en cours...</span>
                  </>
                ) : (
                  <>
                    <Bot className="w-5 h-5" />
                    <span>Tester la connexion</span>
                  </>
                )}
              </button>

              {testMessage && (
                <div className={`p-4 rounded-lg ${
                  testResult === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {testMessage}
                </div>
              )}
            </div>
          </div>

          {/* Documentation */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">
              Documentation
            </h3>
            <div className="prose prose-sm">
              <p>
                L'assistant K-Bot utilise l'API Mistral pour fournir des réponses intelligentes
                sur les transactions immobilières. Il a accès aux données suivantes :
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Transactions (statut, prix, dates, etc.)</li>
                <li>Négociateurs et leurs rôles</li>
                <li>Agences et leurs performances</li>
                <li>Rétrocessions et commissions</li>
              </ul>
              <p className="mt-4 text-gray-600">
                Pour configurer l'API, ajoutez la clé dans le fichier <code>.env
                </code>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}